import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spin, Empty, Tag, Progress } from 'antd';
import {
  FileTextOutlined,
  AppstoreOutlined,
  TeamOutlined,
  PlusOutlined,
  RightOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  InboxOutlined,
  FileDoneOutlined,
  ArrowUpOutlined,
  TagOutlined,
} from '@ant-design/icons';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import { hasPermission } from '../../utils/permissions';
import { fetchAnalyticsDashboardRequest } from '../../redux/actions/analyticsActions';
import { fetchHomeDataRequest } from '../../redux/actions/homeActions';
import { intelligenceAPI } from '../../api/config';
import './Dashboard.css';

const STATUS_META = {
  open:        { label: 'Open',        color: '#faad14' },
  in_delivery: { label: 'In Delivery', color: '#1890ff' },
  delivered:   { label: 'Delivered',   color: '#13c2c2' },
  billed:      { label: 'Billed',      color: '#722ed1' },
  verified:    { label: 'Verified',    color: '#52c41a' },
  closed:      { label: 'Closed',      color: '#389e0d' },
  cancelled:   { label: 'Cancelled',   color: '#ff4d4f' },
};

const fmt = (val) =>
  val != null ? `₹${Number(val).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';

const fmtShort = (val) => {
  if (!val) return '₹0';
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
  return `₹${val}`;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="db-chart-tooltip">
      <p className="db-chart-tooltip-label">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color, margin: '2px 0', fontSize: 12 }}>
          {entry.name}: {fmt(entry.value)}
        </p>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { permissions, user } = useSelector((s) => s.auth);
  const { dashboard, loading: analyticsLoading } = useSelector((s) => s.analytics);
  const { vendors: homeVendors, products: homeProducts, loading: homeLoading } = useSelector((s) => s.home);

  const loading = analyticsLoading || homeLoading;

  const canViewPO       = hasPermission(permissions, 'purchase_order/view');
  const canViewProducts = hasPermission(permissions, 'product/view');
  const canViewVendors  = hasPermission(permissions, 'vendor/view');
  const canCreatePO     = hasPermission(permissions, 'purchase_order/create');
  const canViewChallans = hasPermission(permissions, 'delivery_challan/view');
  const canViewBills    = hasPermission(permissions, 'bill_receipt/view');
  const canViewAnalytics = hasPermission(permissions, 'purchase_order/view');
  const canApprove      = hasPermission(permissions, 'purchase_order/update');

  // New feature widget data (fetched locally, no Redux needed)
  const [pendingApprovalCount, setPendingApprovalCount] = React.useState(null);
  const [paymentStats, setPaymentStats]                 = React.useState(null);
  const [monthComparison, setMonthComparison]           = React.useState(null);

  useEffect(() => {
    dispatch(fetchAnalyticsDashboardRequest({ group_by: 'month', top_n: 5 }));
    dispatch(fetchHomeDataRequest());
  }, [dispatch]);

  // Fetch new-feature widget data
  React.useEffect(() => {
    if (canApprove) {
      import('../../api/config').then(({ approvalsAPI }) => {
        approvalsAPI.getPendingApprovals().then(r => setPendingApprovalCount((r.data || []).length)).catch(() => {});
      });
    }
    if (canViewBills) {
      import('../../api/config').then(({ paymentsAPI }) => {
        paymentsAPI.getStatistics().then(r => setPaymentStats(r.data)).catch(() => {});
      });
    }
    if (canViewAnalytics) {
      intelligenceAPI.getMonthComparison({ months: 2 }).then(r => setMonthComparison(r.data)).catch(() => {});
    }
  }, [canApprove, canViewBills, canViewAnalytics]);

  const summary       = dashboard?.summary;
  const monthlyTrend  = dashboard?.monthly_trend || [];
  const topVendors    = dashboard?.top_vendors || [];
  const topProducts   = dashboard?.top_products || [];
  const firmBreakdown = dashboard?.firm_breakdown || [];

  // Last 6 months of data for the sparkline
  const trendChartData = useMemo(() =>
    [...monthlyTrend]
      .slice(-6)
      .map((m) => ({
        name: `${m.month_name?.slice(0, 3)} ${String(m.year).slice(2)}`,
        Amount: m.total_amount,
        Tax: m.total_tax,
      })),
    [monthlyTrend]
  );

  // Status breakdown sorted by count desc
  const statusRows = useMemo(() => {
    if (!summary?.status_breakdown) return [];
    const total = summary.total_orders || 1;
    return Object.entries(summary.status_breakdown)
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([status, count]) => ({
        status,
        count,
        pct: Math.round((count / total) * 100),
        ...STATUS_META[status],
      }));
  }, [summary]);

  // Insights
  const completionRate = summary?.total_orders
    ? Math.round(((summary.completed_orders || 0) / summary.total_orders) * 100)
    : 0;
  const taxRate = summary?.total_amount
    ? ((summary.total_tax || 0) / summary.total_amount * 100).toFixed(1)
    : '0.0';
  const topVendorShare = useMemo(() => {
    if (!topVendors.length || !summary?.total_amount) return null;
    const top3Sum = topVendors.slice(0, 3).reduce((s, v) => s + (v.total_amount || 0), 0);
    return Math.round((top3Sum / summary.total_amount) * 100);
  }, [topVendors, summary]);

  const greeting = useMemo(() => {
    const h = dayjs().hour();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const quickActions = [
    { title: 'New Purchase Order', desc: 'Raise a purchase order', icon: <PlusOutlined />, color: '#0066cc', path: '/purchase-order', show: canCreatePO },
    { title: 'Delivery Challans', desc: 'Record or track deliveries', icon: <InboxOutlined />, color: '#13c2c2', path: '/delivery-challans', show: canViewChallans },
    { title: 'Bill Receipts', desc: 'Verify and close vendor bills', icon: <FileDoneOutlined />, color: '#722ed1', path: '/bill-receipts', show: canViewBills },
    { title: 'Products', desc: 'Manage the product catalog', icon: <AppstoreOutlined />, color: '#52c41a', path: '/products', show: canViewProducts },
    { title: 'Vendors', desc: 'Manage your vendor list', icon: <TeamOutlined />, color: '#fa8c16', path: '/vendors', show: canViewVendors },
    { title: 'Analytics', desc: 'Deep-dive into spend trends', icon: <BarChartOutlined />, color: '#eb2f96', path: '/analytics', show: canViewAnalytics },
  ].filter((a) => a.show);

  return (
    <div className="page-container">
      {/* ── Welcome Banner ───────────────────────────────── */}
      <div className="db-welcome">
        <div className="db-welcome-text">
          <h1>{greeting}, {user?.username || 'User'}</h1>
          <p>
            {dayjs().format('dddd, D MMMM YYYY')}
            {summary?.total_orders
              ? ` · ${summary.total_orders.toLocaleString('en-IN')} purchase orders · ${fmtShort(summary.total_amount)} total spend`
              : ''}
          </p>
        </div>
      </div>

      <Spin spinning={loading} tip="Loading dashboard…">
        {/* ── KPI Cards ────────────────────────────────────── */}
        <div className="db-kpi-grid">
          {canViewPO && (
            <div className="db-kpi-card" onClick={() => navigate('/purchase-order')} role="button">
              <div className="db-kpi-icon" style={{ background: '#e6f0fa', color: '#0066cc' }}><FileTextOutlined /></div>
              <div className="db-kpi-body">
                <div className="db-kpi-value">{summary?.total_orders?.toLocaleString('en-IN') ?? '—'}</div>
                <div className="db-kpi-label">Total Orders</div>
                {summary && (
                  <div className="db-kpi-sub">{summary.pending_orders} pending</div>
                )}
              </div>
              <RightOutlined className="db-kpi-arrow" />
            </div>
          )}

          {canViewPO && (
            <div className="db-kpi-card" onClick={() => navigate('/analytics')} role="button">
              <div className="db-kpi-icon" style={{ background: '#f0faf0', color: '#28a745' }}><DollarOutlined /></div>
              <div className="db-kpi-body">
                <div className="db-kpi-value">{fmtShort(summary?.total_amount)}</div>
                <div className="db-kpi-label">Total Spend</div>
                {summary && (
                  <div className="db-kpi-sub">{fmt(summary.total_amount)}</div>
                )}
              </div>
              <RightOutlined className="db-kpi-arrow" />
            </div>
          )}

          {canViewPO && (
            <div className="db-kpi-card" onClick={() => navigate('/analytics')} role="button">
              <div className="db-kpi-icon" style={{ background: '#fff7e6', color: '#fa8c16' }}><ArrowUpOutlined /></div>
              <div className="db-kpi-body">
                <div className="db-kpi-value">{fmtShort(summary?.avg_order_value)}</div>
                <div className="db-kpi-label">Avg Order Value</div>
                <div className="db-kpi-sub">{fmtShort(summary?.total_tax)} tax</div>
              </div>
              <RightOutlined className="db-kpi-arrow" />
            </div>
          )}

          {canViewPO && (
            <div className="db-kpi-card" onClick={() => navigate('/purchase-order')} role="button">
              <div className="db-kpi-icon" style={{ background: '#fff1f0', color: '#ff4d4f' }}><ClockCircleOutlined /></div>
              <div className="db-kpi-body">
                <div className="db-kpi-value">{summary?.pending_orders ?? '—'}</div>
                <div className="db-kpi-label">Pending Orders</div>
                <div className="db-kpi-sub">Open + In Delivery</div>
              </div>
              <RightOutlined className="db-kpi-arrow" />
            </div>
          )}

          {canViewProducts && (
            <div className="db-kpi-card" onClick={() => navigate('/products')} role="button">
              <div className="db-kpi-icon" style={{ background: '#f5f0ff', color: '#722ed1' }}><AppstoreOutlined /></div>
              <div className="db-kpi-body">
                <div className="db-kpi-value">{homeProducts.length || '—'}</div>
                <div className="db-kpi-label">Products</div>
                <div className="db-kpi-sub">In catalog</div>
              </div>
              <RightOutlined className="db-kpi-arrow" />
            </div>
          )}

          {canViewVendors && (
            <div className="db-kpi-card" onClick={() => navigate('/vendors')} role="button">
              <div className="db-kpi-icon" style={{ background: '#e6fffb', color: '#13c2c2' }}><TeamOutlined /></div>
              <div className="db-kpi-body">
                <div className="db-kpi-value">{homeVendors.length || '—'}</div>
                <div className="db-kpi-label">Vendors</div>
                <div className="db-kpi-sub">Registered</div>
              </div>
              <RightOutlined className="db-kpi-arrow" />
            </div>
          )}
        </div>

        {/* ── New Feature Widget Cards ─────────────────────── */}
        <div className="db-kpi-grid" style={{ marginTop: 0 }}>
          {/* Pending Approvals */}
          {canApprove && pendingApprovalCount !== null && (
            <div
              className="db-kpi-card"
              onClick={() => navigate('/approvals')}
              role="button"
              style={pendingApprovalCount > 0 ? { borderLeft: '3px solid #faad14' } : {}}
            >
              <div className="db-kpi-icon" style={{ background: '#fffbe6', color: '#faad14' }}>
                <CheckCircleOutlined />
              </div>
              <div className="db-kpi-body">
                <div className="db-kpi-value" style={{ color: pendingApprovalCount > 0 ? '#faad14' : '#52c41a' }}>
                  {pendingApprovalCount}
                </div>
                <div className="db-kpi-label">Pending Approvals</div>
                <div className="db-kpi-sub">
                  {pendingApprovalCount > 0 ? 'Orders need review' : 'All clear'}
                </div>
              </div>
              <RightOutlined className="db-kpi-arrow" />
            </div>
          )}

          {/* Outstanding Payments */}
          {canViewBills && paymentStats && (
            <div
              className="db-kpi-card"
              onClick={() => navigate('/payments')}
              role="button"
              style={paymentStats.total_outstanding > 0 ? { borderLeft: '3px solid #ff4d4f' } : {}}
            >
              <div className="db-kpi-icon" style={{ background: '#fff2f0', color: '#ff4d4f' }}>
                <DollarOutlined />
              </div>
              <div className="db-kpi-body">
                <div className="db-kpi-value" style={{ color: paymentStats.total_outstanding > 0 ? '#ff4d4f' : '#52c41a', fontSize: 16 }}>
                  {fmtShort(paymentStats.total_outstanding)}
                </div>
                <div className="db-kpi-label">Outstanding Payments</div>
                <div className="db-kpi-sub">{fmtShort(paymentStats.total_amount_paid)} paid so far</div>
              </div>
              <RightOutlined className="db-kpi-arrow" />
            </div>
          )}

          {/* Month Spend Comparison */}
          {canViewAnalytics && monthComparison?.months?.length >= 2 && (() => {
            const curr = monthComparison.months[monthComparison.months.length - 1];
            const prev = monthComparison.months[monthComparison.months.length - 2];
            const diff = (curr?.total_amount || 0) - (prev?.total_amount || 0);
            const pct  = prev?.total_amount ? ((diff / prev.total_amount) * 100).toFixed(1) : null;
            const up   = diff > 0;
            return (
              <div
                className="db-kpi-card"
                onClick={() => navigate('/analytics/month-comparison')}
                role="button"
              >
                <div className="db-kpi-icon" style={{ background: '#e6f7ff', color: '#1890ff' }}>
                  <BarChartOutlined />
                </div>
                <div className="db-kpi-body">
                  <div className="db-kpi-value" style={{ fontSize: 16 }}>{fmtShort(curr?.total_amount)}</div>
                  <div className="db-kpi-label">This Month Spend</div>
                  {pct !== null && (
                    <div className="db-kpi-sub" style={{ color: up ? '#ff4d4f' : '#52c41a' }}>
                      {up ? '↑' : '↓'} {Math.abs(pct)}% vs last month
                    </div>
                  )}
                </div>
                <RightOutlined className="db-kpi-arrow" />
              </div>
            );
          })()}
        </div>

        {/* ── Charts Row ───────────────────────────────────── */}
        {canViewPO && (
          <div className="db-charts-row">
            {/* Spend Trend */}
            <div className="db-card db-card--wide">
              <div className="db-card-header">
                <h3>Spend Trend <span className="db-card-sub">(last 6 months)</span></h3>
                <button className="db-link-btn" onClick={() => navigate('/analytics')}>
                  Full analytics <RightOutlined />
                </button>
              </div>
              {trendChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={trendChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="dbGradAmount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#0066cc" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#0066cc" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="dbGradTax" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#13c2c2" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#13c2c2" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={52} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="Amount" stroke="#0066cc" fill="url(#dbGradAmount)" strokeWidth={2} dot={false} />
                    <Area type="monotone" dataKey="Tax"    stroke="#13c2c2" fill="url(#dbGradTax)"    strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No trend data yet" style={{ padding: '40px 0' }} />
              )}
            </div>

            {/* Order Status Breakdown */}
            <div className="db-card">
              <div className="db-card-header">
                <h3>Order Status</h3>
                {summary && (
                  <span className="db-card-sub">{summary.total_orders} total</span>
                )}
              </div>
              {statusRows.length > 0 ? (
                <div className="db-status-list">
                  {statusRows.map((s) => (
                    <div key={s.status} className="db-status-row">
                      <div className="db-status-label">
                        <span className="db-status-dot" style={{ background: s.color }} />
                        <span>{s.label}</span>
                      </div>
                      <div className="db-status-bar-wrap">
                        <Progress
                          percent={s.pct}
                          showInfo={false}
                          strokeColor={s.color}
                          trailColor="#f5f5f5"
                          size="small"
                          style={{ margin: 0 }}
                        />
                      </div>
                      <span className="db-status-count">{s.count}</span>
                    </div>
                  ))}
                  <div className="db-status-footer">
                    <Tag color="green" icon={<CheckCircleOutlined />}>{summary?.completed_orders || 0} completed</Tag>
                    <Tag color="orange" icon={<ClockCircleOutlined />}>{summary?.pending_orders || 0} pending</Tag>
                  </div>
                </div>
              ) : (
                <Empty description="No orders yet" style={{ padding: '40px 0' }} />
              )}
            </div>
          </div>
        )}

        {/* ── Insights + Top Lists Row ─────────────────────── */}
        <div className="db-bottom-row">
          {/* Insights Panel */}
          {canViewPO && summary?.total_orders > 0 && (
            <div className="db-card db-insights-card">
              <div className="db-card-header">
                <h3>Insights</h3>
              </div>
              <div className="db-insights-list">
                <div className="db-insight-item">
                  <div className="db-insight-icon" style={{ background: '#f6ffed', color: '#389e0d' }}>
                    <CheckCircleOutlined />
                  </div>
                  <div className="db-insight-body">
                    <div className="db-insight-value">{completionRate}%</div>
                    <div className="db-insight-label">Order completion rate</div>
                  </div>
                </div>
                <div className="db-insight-item">
                  <div className="db-insight-icon" style={{ background: '#fff7e6', color: '#fa8c16' }}>
                    <DollarOutlined />
                  </div>
                  <div className="db-insight-body">
                    <div className="db-insight-value">{taxRate}%</div>
                    <div className="db-insight-label">Tax as % of spend</div>
                  </div>
                </div>
                {summary?.total_discount > 0 && (
                  <div className="db-insight-item">
                    <div className="db-insight-icon" style={{ background: '#f5f0ff', color: '#722ed1' }}>
                      <TagOutlined />
                    </div>
                    <div className="db-insight-body">
                      <div className="db-insight-value">{fmtShort(summary.total_discount)}</div>
                      <div className="db-insight-label">Total discounts saved</div>
                    </div>
                  </div>
                )}
                {topVendorShare != null && (
                  <div className="db-insight-item">
                    <div className="db-insight-icon" style={{ background: '#e6f0fa', color: '#0066cc' }}>
                      <TeamOutlined />
                    </div>
                    <div className="db-insight-body">
                      <div className="db-insight-value">{topVendorShare}%</div>
                      <div className="db-insight-label">Spend with top 3 vendors</div>
                    </div>
                  </div>
                )}
                {firmBreakdown.length > 1 && (
                  <div className="db-insight-item">
                    <div className="db-insight-icon" style={{ background: '#e6fffb', color: '#13c2c2' }}>
                      <FileTextOutlined />
                    </div>
                    <div className="db-insight-body">
                      <div className="db-insight-value">{firmBreakdown.length}</div>
                      <div className="db-insight-label">Buying firms active</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Top Vendors */}
          {canViewPO && topVendors.length > 0 && (
            <div className="db-card">
              <div className="db-card-header">
                <h3>Top Vendors</h3>
                <button className="db-link-btn" onClick={() => navigate('/analytics')}>
                  See all <RightOutlined />
                </button>
              </div>
              <div className="db-top-list">
                {topVendors.slice(0, 5).map((v, i) => (
                  <div key={v.vendor_id} className="db-top-row">
                    <span className="db-top-rank">#{i + 1}</span>
                    <div className="db-top-body">
                      <div className="db-top-name">{v.vendor_name}</div>
                      <div className="db-top-meta">{v.total_orders} orders</div>
                    </div>
                    <div className="db-top-amount">{fmtShort(v.total_amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Products */}
          {canViewPO && topProducts.length > 0 && (
            <div className="db-card">
              <div className="db-card-header">
                <h3>Top Products</h3>
                <button className="db-link-btn" onClick={() => navigate('/analytics')}>
                  See all <RightOutlined />
                </button>
              </div>
              <div className="db-top-list">
                {topProducts.slice(0, 5).map((p, i) => (
                  <div key={p.product_id} className="db-top-row">
                    <span className="db-top-rank">#{i + 1}</span>
                    <div className="db-top-body">
                      <div className="db-top-name">{p.product_name}</div>
                      <div className="db-top-meta">{p.total_quantity?.toLocaleString('en-IN')} {p.unit}</div>
                    </div>
                    <div className="db-top-amount">{fmtShort(p.total_amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Quick Actions ────────────────────────────────── */}
        <div className="db-card db-quick-actions-card">
          <div className="db-card-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="db-quick-grid">
            {quickActions.map((action) => (
              <div
                key={action.path}
                className="db-quick-item"
                onClick={() => navigate(action.path)}
                style={{ '--accent': action.color }}
                role="button"
              >
                <div className="db-quick-icon" style={{ background: `${action.color}18`, color: action.color }}>
                  {action.icon}
                </div>
                <div className="db-quick-body">
                  <div className="db-quick-title">{action.title}</div>
                  <div className="db-quick-desc">{action.desc}</div>
                </div>
                <RightOutlined className="db-quick-arrow" />
              </div>
            ))}
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default Dashboard;
